// Roadmap — milestone overview rendered as a list of timeline rows with
// progress bars + per-milestone date windows. Sits between the SteerBoard
// and the foot accordions.

import { useCallback, useMemo, useState, type JSX } from "react";

import type {
  CreateMilestoneRequest,
  DashboardMilestone,
  MilestoneRoadmapRow,
} from "@loom/shared/dashboard";

import { useT, type Translator } from "../../i18n/index.js";
import { AutoGrowTextarea } from "../../ui/AutoGrowTextarea";

import { Icon } from "./Icon";

interface RoadmapProps {
  readonly rows: ReadonlyArray<MilestoneRoadmapRow>;
  readonly loading: boolean;
  readonly creating: boolean;
  readonly onCreate: (request: CreateMilestoneRequest) => Promise<DashboardMilestone>;
  readonly onSelectMilestone?: (milestoneId: number) => void;
}

const fmtRange = (m: DashboardMilestone, t: Translator): string => {
  if (!m.startDate && !m.dueDate) return t("dashboard.roadmap.noWindow");
  const start = m.startDate ?? "—";
  const due = m.dueDate ?? "—";
  return `${start} → ${due}`;
};

/** null when this row carries no counts — the response was fetched with
 *  `?counts=none`. A 0% bar for an uncounted milestone is a measurement claim
 *  nobody made, so the caller renders no bar at all rather than a zeroed one. */
const progressPct = (row: MilestoneRoadmapRow): number | null => {
  if (row.issueCount === undefined || row.closedCount === undefined) return null;
  if (row.issueCount === 0) return 0;
  return Math.round((row.closedCount / row.issueCount) * 100);
};

const STATE_COLOR: Record<DashboardMilestone["state"], string> = {
  active: "var(--st-open)",
  closed: "var(--st-done)",
};

interface DraftState {
  readonly title: string;
  readonly description: string;
  readonly startDate: string;
  readonly dueDate: string;
}

const EMPTY_DRAFT: DraftState = {
  title: "",
  description: "",
  startDate: "",
  dueDate: "",
};

export const Roadmap = ({
  rows,
  loading,
  creating,
  onCreate,
  onSelectMilestone,
}: RoadmapProps): JSX.Element => {
  const t = useT();
  const [formOpen, setFormOpen] = useState(false);
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [error, setError] = useState<string | null>(null);

  const sorted = useMemo(() => rows.slice(), [rows]);

  const submit = useCallback(async (): Promise<void> => {
    setError(null);
    const title = draft.title.trim();
    if (title.length === 0) {
      setError(t("dashboard.roadmap.titleRequired"));
      return;
    }
    const request: CreateMilestoneRequest = {
      title,
      ...(draft.description.trim().length > 0
        ? { description: draft.description.trim() }
        : {}),
      ...(draft.startDate.length > 0 ? { startDate: draft.startDate } : {}),
      ...(draft.dueDate.length > 0 ? { dueDate: draft.dueDate } : {}),
    };
    try {
      await onCreate(request);
      setDraft(EMPTY_DRAFT);
      setFormOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [draft, onCreate, t]);

  return (
    <section className="roadmap" data-testid="roadmap-section">
      <div className="steer-head">
        <div className="steer-head-left">
          <span className="steer-eyebrow">
            <Icon name="sliders" size={12} /> {t("dashboard.roadmap.eyebrow")}
          </span>
          <h2 className="steer-title">{t("dashboard.roadmap.title")}</h2>
          <span className="steer-sub">
            {t("dashboard.roadmap.sub")}
          </span>
        </div>
        <div className="steer-head-right">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            data-testid="roadmap-new-milestone"
            onClick={() => setFormOpen((open) => !open)}
          >
            <Icon name="plus" size={12} />
            <span>{formOpen ? t("dashboard.roadmap.cancel") : t("dashboard.roadmap.newMilestone")}</span>
          </button>
        </div>
      </div>

      {formOpen && (
        <div className="roadmap-form" data-testid="roadmap-form">
          <label className="roadmap-field">
            <span>{t("dashboard.roadmap.fieldTitle")}</span>
            <AutoGrowTextarea
              value={draft.title}
              onChange={(e) =>
                setDraft((d) => ({ ...d, title: e.target.value }))
              }
              placeholder={t("dashboard.roadmap.titlePlaceholder")}
              data-testid="roadmap-form-title"
              autoFocus
            />
          </label>
          <label className="roadmap-field">
            <span>{t("dashboard.roadmap.fieldDescription")}</span>
            <AutoGrowTextarea
              minLines={3}
              value={draft.description}
              onChange={(e) =>
                setDraft((d) => ({ ...d, description: e.target.value }))
              }
              placeholder={t("dashboard.roadmap.descriptionPlaceholder")}
              data-testid="roadmap-form-description"
            />
          </label>
          <div className="roadmap-form-row">
            <label className="roadmap-field">
              <span>{t("dashboard.roadmap.fieldStart")}</span>
              <input
                type="date"
                value={draft.startDate}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, startDate: e.target.value }))
                }
                data-testid="roadmap-form-start"
              />
            </label>
            <label className="roadmap-field">
              <span>{t("dashboard.roadmap.fieldDue")}</span>
              <input
                type="date"
                value={draft.dueDate}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, dueDate: e.target.value }))
                }
                data-testid="roadmap-form-due"
              />
            </label>
          </div>
          {error && (
            <p className="roadmap-error" role="alert">
              {error}
            </p>
          )}
          <div className="roadmap-form-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={creating}
              onClick={() => void submit()}
              data-testid="roadmap-form-submit"
            >
              {creating ? t("dashboard.roadmap.creating") : t("dashboard.roadmap.createMilestone")}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="muted" data-testid="roadmap-loading">
          {t("dashboard.roadmap.loadingMilestones")}
        </p>
      ) : sorted.length === 0 ? (
        <p className="muted" data-testid="roadmap-empty">
          {t("dashboard.roadmap.empty")}
        </p>
      ) : (
        <ul className="roadmap-list" data-testid="roadmap-list">
          {sorted.map((row) => {
            const pct = progressPct(row);
            const clickable = onSelectMilestone !== undefined;
            return (
              <li
                key={row.milestone.id}
                className={`roadmap-row roadmap-row-${row.milestone.state}${clickable ? "" : " roadmap-row-static"}`}
                data-testid={`roadmap-row-${row.milestone.iid}`}
                {...(clickable
                  ? { onClick: () => onSelectMilestone(row.milestone.id) }
                  : {})}
              >
                <div className="roadmap-row-head">
                  <span
                    className="roadmap-pip"
                    style={{ background: STATE_COLOR[row.milestone.state] }}
                    aria-hidden="true"
                  />
                  <h3 className="roadmap-title">
                    {row.milestone.webUrl ? (
                      <a
                        href={row.milestone.webUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {row.milestone.title}
                      </a>
                    ) : (
                      row.milestone.title
                    )}
                  </h3>
                  <span className="roadmap-state" data-state={row.milestone.state}>
                    {row.milestone.state}
                  </span>
                </div>
                <div className="roadmap-meta">
                  <span>{fmtRange(row.milestone, t)}</span>
                  {/* The count keys are absent on a `?counts=none` response.
                      Rendering nothing is the only honest option: "0 of 0" and
                      a 0% bar are measurements, and a reader cannot tell a
                      fabricated one from a real one. This panel always asks
                      WITH counts, so the branch is the type system holding the
                      line rather than a state users reach. */}
                  {row.issueCount !== undefined && row.closedCount !== undefined && (
                    <span>
                      {t("dashboard.roadmap.issuesCount", {
                        closed: row.closedCount,
                        total: row.issueCount,
                      })}
                      {typeof row.weight === "number"
                        ? ` · ${t("dashboard.roadmap.points", { weight: row.weight })}`
                        : ""}
                    </span>
                  )}
                </div>
                {pct !== null && (
                  <div className="roadmap-bar" role="progressbar" aria-valuenow={pct}>
                    <div
                      className="roadmap-bar-fill"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                )}
                {row.milestone.description && (
                  <p className="roadmap-desc">{row.milestone.description}</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
